import { useState } from "react";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Upload, FileText, Trash2, ExternalLink } from "lucide-react";

export type ProfileDocKind = "cv" | "competencyCard" | "awards" | "other";

export type ProfileDocument = {
  kind: ProfileDocKind;
  name: string;
  url: string;
  path: string;
  size: number;
  uploadedAt: number;
};

const LABELS: Record<ProfileDocKind, { en: string; fr: string; desc: { en: string; fr: string } }> = {
  cv: {
    en: "CV / Resume", fr: "CV",
    desc: { en: "Your professional resume", fr: "Votre CV professionnel" },
  },
  competencyCard: {
    en: "Competency Card", fr: "Carte de compétence",
    desc: { en: "Industry certification or competency card", fr: "Certification ou carte de compétence" },
  },
  awards: {
    en: "Awards / Honors", fr: "Prix / Distinctions",
    desc: { en: "Recognitions and honors", fr: "Reconnaissances et distinctions" },
  },
  other: {
    en: "Other", fr: "Autre",
    desc: { en: "Any other relevant document", fr: "Tout autre document pertinent" },
  },
};

export function ProfileDocuments({
  documents,
  saving,
  onSave,
}: {
  documents: ProfileDocument[] | undefined;
  saving: boolean;
  onSave: (docs: ProfileDocument[]) => Promise<void>;
}) {
  const { user } = useAuth();
  const { language } = useLanguage();
  const lang = (language as "en" | "fr") || "en";
  const { toast } = useToast();
  const [uploadingKind, setUploadingKind] = useState<ProfileDocKind | null>(null);
  const docs = documents || [];

  const t = lang === "fr"
    ? { title: "Documents complémentaires (optionnel)", subtitle: "Téléversez CV, cartes de compétence, distinctions ou autres documents pertinents", upload: "Téléverser", uploaded: "Document téléversé", removed: "Document supprimé", error: "Erreur", view: "Voir" }
    : { title: "Supporting Documents (optional)", subtitle: "Upload CV, competency cards, awards, or other relevant documents for this sector", upload: "Upload", uploaded: "Document uploaded", removed: "Document removed", error: "Error", view: "View" };

  const handleUpload = async (kind: ProfileDocKind, file: File) => {
    if (!user?.id) return;
    console.log('[ProfileDocuments] uploading to path:',
      `profileDocuments/${user.id}/${kind}-${Date.now()}.ext`
    );
    setUploadingKind(kind);
    try {
      const ext = file.name.split(".").pop() || "bin";
      const path = `profileDocuments/${user.id}/${kind}-${Date.now()}.${ext}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, file, { contentType: file.type });
      const url = await getDownloadURL(r);
      const next: ProfileDocument[] = [
        ...docs,
        { kind, name: file.name, url, path, size: file.size, uploadedAt: Date.now() },
      ];
      await onSave(next);
      toast({ title: t.uploaded });
    } catch (err) {
      console.error("[ProfileDocuments] upload error", {
        code: (err as any)?.code,
        message: (err as Error)?.message,
        serverResponse: (err as any)?.serverResponse,
      });
      toast({ title: t.error, description: String((err as Error)?.message || ""), variant: "destructive" });
    } finally {
      setUploadingKind(null);
    }
  };

  const handleRemove = async (d: ProfileDocument) => {
    try {
      try { await deleteObject(storageRef(storage, d.path)); } catch { /* ignore */ }
      const next = docs.filter((x) => x.path !== d.path);
      await onSave(next);
      toast({ title: t.removed });
    } catch (err) {
      toast({ title: t.error, description: String((err as Error)?.message || ""), variant: "destructive" });
    }
  };

  const renderSlot = (kind: ProfileDocKind) => {
    const items = docs.filter((d) => d.kind === kind);
    const meta = LABELS[kind];
    return (
      <div key={kind} className="border rounded-lg p-4 space-y-3">
        <div>
          <Label className="font-medium">{meta[lang]}</Label>
          <p className="text-xs text-muted-foreground">{meta.desc[lang]}</p>
        </div>
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.path} className="flex items-center justify-between text-sm bg-muted/40 rounded px-2 py-1">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{d.name}</span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button asChild variant="ghost" size="sm">
                  <a href={d.url} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleRemove(d)} disabled={saving}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <label className="inline-flex">
          <input
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(kind, f); e.target.value = ""; }}
          />
          <Button asChild variant="outline" size="sm" disabled={uploadingKind === kind || saving}>
            <span>
              {uploadingKind === kind ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {t.upload}
            </span>
          </Button>
        </label>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t.title}</CardTitle>
        <p className="text-sm text-muted-foreground">{t.subtitle}</p>
      </CardHeader>
      <CardContent className="grid md:grid-cols-2 gap-4">
        {(["cv", "competencyCard", "awards", "other"] as ProfileDocKind[]).map(renderSlot)}
      </CardContent>
    </Card>
  );
}
